import type { ButtonHTMLAttributes, FormEvent, InputHTMLAttributes, ReactNode } from 'react';
import { IconClose, IconSearch } from '@/components/icons';
import { VISIBILITY_OPTIONS, type SelectOption } from '@/components/panelOptions';
import { formatMoneyAmount } from '@/utils/format';
import { STATUS_LABELS, statusLabel } from '@/utils/labels';

export function Loading({ label = 'جاري التحميل…' }: { label?: string }) {
  return (
    <div className="state state-loading" role="status">
      <div className="spinner" aria-hidden />
      <p>{label}</p>
    </div>
  );
}

export function Empty({ title = 'لا توجد بيانات', hint }: { title?: string; hint?: string }) {
  return (
    <div className="empty-state">
      <strong>{title}</strong>
      {hint ? <p>{hint}</p> : null}
    </div>
  );
}

export function ErrorBox({
  message,
  onRetry,
}: {
  message: string;
  onRetry?: () => void;
}) {
  return (
    <div className="state state-error" role="alert">
      <p className="state-title">{message}</p>
      {onRetry ? (
        <button type="button" className="btn btn-secondary" onClick={onRetry}>
          إعادة المحاولة
        </button>
      ) : null}
    </div>
  );
}

export function Card({
  children,
  className = '',
  onClick,
}: {
  children: ReactNode;
  className?: string;
  onClick?: () => void;
}) {
  if (onClick) {
    return (
      <button type="button" className={`card card-btn ${className}`.trim()} onClick={onClick}>
        {children}
      </button>
    );
  }
  return <div className={`card ${className}`.trim()}>{children}</div>;
}

/** Shared panel button — primary/danger use white label text via design-system CSS. */
export function Button({
  children,
  variant = 'primary',
  className = '',
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: 'primary' | 'secondary' | 'danger' }) {
  const variantClass =
    variant === 'primary' ? 'btn btn-primary' : variant === 'danger' ? 'btn btn-danger' : 'btn btn-secondary';
  return (
    <button className={`${variantClass} ${className}`.trim()} {...rest}>
      {children}
    </button>
  );
}

export function Input({
  label,
  hint,
  inputMode,
  dir,
  lang,
  className = '',
  ...rest
}: InputHTMLAttributes<HTMLInputElement> & { label: string; hint?: string }) {
  const numeric = inputMode === 'decimal' || inputMode === 'numeric' || inputMode === 'tel';
  return (
    <label className={`field ui-field ${className}`.trim()}>
      <span>{label}</span>
      <input
        className="ui-control"
        inputMode={inputMode}
        dir={dir ?? (numeric ? 'ltr' : undefined)}
        lang={lang ?? (numeric ? 'en' : undefined)}
        {...rest}
      />
      {hint ? <small className="field-hint">{hint}</small> : null}
    </label>
  );
}

export function Textarea({
  label,
  value,
  onChange,
  rows = 4,
  required,
  placeholder,
  hint,
  className = '',
  disabled,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  rows?: number;
  required?: boolean;
  placeholder?: string;
  hint?: string;
  className?: string;
  disabled?: boolean;
}) {
  return (
    <label className={`field ui-field ${className}`.trim()}>
      <span>{label}</span>
      <textarea
        className="ui-control"
        rows={rows}
        value={value}
        required={required}
        placeholder={placeholder}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
      />
      {hint ? <small className="field-hint">{hint}</small> : null}
    </label>
  );
}

export function Select({
  label,
  value,
  onChange,
  options,
  required,
  disabled,
  className = '',
  hint,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: SelectOption[];
  required?: boolean;
  disabled?: boolean;
  className?: string;
  hint?: string;
}) {
  return (
    <label className={`field ui-field ${className}`.trim()}>
      <span>{label}</span>
      <select
        className="ui-control"
        value={value}
        required={required}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
      >
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
      {hint ? <small className="field-hint">{hint}</small> : null}
    </label>
  );
}

/** Visibility / active toggle used across admin forms. */
export function VisibilitySelect({
  label = 'الحالة',
  value,
  onChange,
  options = VISIBILITY_OPTIONS,
  className = '',
}: {
  label?: string;
  value: string | number;
  onChange: (v: string) => void;
  options?: SelectOption[];
  className?: string;
}) {
  return (
    <Select
      label={label}
      value={String(value)}
      onChange={onChange}
      options={options}
      className={className}
    />
  );
}

export function Checkbox({
  label,
  checked,
  onChange,
  disabled,
  className = '',
}: {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
  className?: string;
}) {
  return (
    <label className={`check-line ui-check ${className}`.trim()}>
      <input
        type="checkbox"
        className="ui-control-check"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span>{label}</span>
    </label>
  );
}

export function FlashMessage({
  message,
  tone,
}: {
  message?: string | null;
  /** When omitted, success is inferred from Arabic "تم". */
  tone?: 'ok' | 'error';
}) {
  if (!message) return null;
  const resolved =
    tone ?? (message.includes('تم') || message.includes('نجاح') ? 'ok' : 'error');
  return <p className={resolved === 'ok' ? 'ok' : 'error'}>{message}</p>;
}

export function FormActions({
  children,
  className = '',
}: {
  children: ReactNode;
  className?: string;
}) {
  return <div className={`form-actions action-row wrap top-space ${className}`.trim()}>{children}</div>;
}

export function Modal({
  open,
  title,
  subtitle,
  children,
  onClose,
  wide,
}: {
  open: boolean;
  title: string;
  subtitle?: string;
  children: ReactNode;
  onClose: () => void;
  wide?: boolean;
}) {
  if (!open) return null;
  return (
    <div className="modal-backdrop" role="presentation" onClick={onClose}>
      <section
        className={`admin-modal ${wide ? 'wide' : ''}`.trim()}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-head">
          <div>
            <h2>{title}</h2>
            {subtitle ? <p>{subtitle}</p> : null}
          </div>
          <button type="button" className="icon-btn" onClick={onClose} aria-label="إغلاق">
            <IconClose size={18} />
          </button>
        </div>
        <div className="modal-body">{children}</div>
      </section>
    </div>
  );
}

export function Form({
  children,
  onSubmit,
  className = '',
}: {
  children: ReactNode;
  onSubmit: (e: FormEvent) => void;
  className?: string;
}) {
  return (
    <form
      className={`form panel-form ${className}`.trim()}
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit(e);
      }}
    >
      {children}
    </form>
  );
}

export function PageHeader({
  title,
  subtitle,
  actions,
  icon,
}: {
  title: string;
  subtitle?: string;
  actions?: ReactNode;
  icon?: ReactNode;
}) {
  return (
    <section className="admin-page-header">
      <div className="admin-page-title">
        {icon ? <span className="section-icon">{icon}</span> : null}
        <div>
          <h1>{title}</h1>
          {subtitle ? <p>{subtitle}</p> : null}
        </div>
      </div>
      {actions ? <div className="admin-page-actions">{actions}</div> : null}
    </section>
  );
}

export function ProSearch({
  value,
  onChange,
  placeholder = 'بحث...',
  onSubmit,
  className = '',
  disabled,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  onSubmit?: () => void;
  className?: string;
  disabled?: boolean;
}) {
  return (
    <label className={`pro-search panel-search ui-search ${className}`.trim()}>
      <IconSearch size={18} aria-hidden />
      <input
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            onSubmit?.();
          }
        }}
        placeholder={placeholder}
        autoComplete="off"
        aria-label={placeholder}
      />
    </label>
  );
}

function normalizeMoneySuffix(suffix: string): string {
  const s = suffix.trim();
  const upper = s.toUpperCase();
  if (upper === 'USD' || s === '$') return '$';
  if (upper === 'SYP' || s === 'ل.س' || s === 'ليرة') return 'ل.س';
  return s;
}

export function Money({
  value,
  suffix = 'ل.س',
  fractionDigits,
}: {
  value: unknown;
  suffix?: string;
  fractionDigits?: number;
}) {
  const text = formatMoneyAmount(value, fractionDigits);
  const currency = suffix ? normalizeMoneySuffix(suffix) : '';
  // Physical order locked via flex + bdi (Arabic "ل.س" must not bidi-flip past digits).
  return (
    <span className="money" dir="ltr" lang="en">
      {currency ? <bdi className="money-currency">{currency}</bdi> : null}
      <bdi className="money-amount">{text}</bdi>
    </span>
  );
}

export { STATUS_LABELS };

export function StatusBadge({ status }: { status: unknown }) {
  const s = String(status ?? '').toLowerCase();
  return <span className={`status-pill ${s || 'unknown'}`}>{statusLabel(status)}</span>;
}

export function Row({
  label,
  value,
}: {
  label: string;
  value: ReactNode;
}) {
  return (
    <div className="kv-row">
      <span className="kv-label">{label}</span>
      <span className="kv-value">{value}</span>
    </div>
  );
}
